/**
 * FederalProfileCard.js — single-source-of-truth for Moovin Forward's federal
 * contracting identity (UEI, CAGE, NAICS codes, SBA size std, certifications).
 *
 * Auto-loads current profile + NAICS reference catalog. Shannon:
 *   1. Pastes UEI + CAGE
 *   2. Picks primary NAICS from dropdown (or types a 6-digit code)
 *   3. Adds any number of secondary NAICS
 *   4. Saves — every backend engine (SAM.gov, MWBE, business-structure PDF)
 *      immediately reads from this profile.
 */
import React, { useEffect, useState, useMemo } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CYAN = "#00CFCF";
const CHROME = "#A0A0A0";
const INK = "#F5F5F7";

const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(16px,3vw,26px)", marginBottom: 20 };
const label = { fontSize: 10, letterSpacing: "0.14em", color: CHROME, textTransform: "uppercase", marginBottom: 4, display: "block" };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, borderRadius: 6, padding: "8px 10px", color: INK, fontSize: 13, fontFamily: "monospace" };
const select = { ...input, fontFamily: "inherit" };
const btn = { background: CYAN, color: "#020A1F", border: 0, padding: "10px 18px", fontWeight: 800, borderRadius: 8, cursor: "pointer", fontSize: 12, letterSpacing: "0.04em" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };
const chip = { background: "rgba(0,207,207,0.14)", border: `1px solid ${CYAN}66`, color: INK, padding: "4px 10px", borderRadius: 999, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, marginRight: 6, marginBottom: 6 };
const chipRemove = { background: "transparent", border: 0, color: CHROME, cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 };

const SAM_STATUS_OPTS = [
  { v: "not_registered", label: "Not yet registered" },
  { v: "pending",        label: "Registration pending" },
  { v: "active",         label: "Active on SAM.gov" },
  { v: "expired",        label: "Expired · needs renewal" },
];

const DEFAULT_CERTS = [
  "TX-HUB", "SBA-WOSB", "SBA 8(a)", "EDWOSB", "WBENC", "NMSDC",
  "HUD Section 3", "HUD-VASH", "DFW-MSDC", "Dallas BID/BEP",
  "Fort Worth M/WBE", "TUCP DBE",
];

export default function FederalProfileCard() {
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [newSecondaryCode, setNewSecondaryCode] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/federal-profile`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setProfile(d.profile); setCatalog(d.naics_catalog || []); })
      .catch(e => setError(`Load failed (${e}). Sign in as admin.`));
  }, []);

  const catalogByCode = useMemo(() => {
    const m = {};
    catalog.forEach(n => { m[n.code] = n; });
    return m;
  }, [catalog]);

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const setPrimaryFromCode = (code) => {
    const c = code.trim();
    const found = catalogByCode[c];
    set("primary_naics", found ? { code: c, title: found.title, note: found.note || "" } : { code: c, title: "", note: "" });
  };

  const addSecondary = () => {
    const c = newSecondaryCode.trim();
    if (!/^\d{6}$/.test(c)) { setError("Secondary NAICS must be exactly 6 digits."); return; }
    if ((profile.secondary_naics || []).some(s => s.code === c)) { setError(`${c} is already listed.`); return; }
    if (profile.primary_naics?.code === c) { setError(`${c} is already the primary NAICS.`); return; }
    const found = catalogByCode[c];
    setError("");
    set("secondary_naics", [...(profile.secondary_naics || []), { code: c, title: found?.title || "", note: found?.note || "" }]);
    setNewSecondaryCode("");
  };

  const removeSecondary = (code) => {
    set("secondary_naics", (profile.secondary_naics || []).filter(s => s.code !== code));
  };

  const toggleCert = (c) => {
    const list = profile.certifications || [];
    set("certifications", list.includes(c) ? list.filter(x => x !== c) : [...list, c]);
  };

  const save = async () => {
    setSaving(true); setError(""); setSavedAt("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/federal-profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uei: profile.uei,
          cage_code: profile.cage_code,
          sam_status: profile.sam_status,
          primary_naics: profile.primary_naics,
          secondary_naics: profile.secondary_naics,
          sba_size_standard_usd: profile.sba_size_standard_usd,
          certifications: profile.certifications,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Save failed (${r.status})`);
      setProfile(d.profile);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !profile) {
    return <div style={{ ...card, borderColor: "#ef4444" }} data-testid="federal-profile-error">{error}</div>;
  }
  if (!profile) return null;

  return (
    <div style={card} data-testid="federal-profile-card">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 4, fontWeight: 800 }}>
        FEDERAL CONTRACTING PROFILE · SINGLE SOURCE OF TRUTH
      </div>
      <div style={{ fontSize: 12, color: CHROME, marginBottom: 18 }}>
        SAM.gov engine + MWBE certifications + business-structure PDF all read from here.
        When your UEI clears, paste it below and every downstream system picks it up.
      </div>

      {/* Identifiers row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 18 }}>
        <div>
          <label style={label}>UEI (12 alphanumeric)</label>
          <input data-testid="fp-uei-input" style={input} maxLength={12}
                 value={profile.uei || ""} onChange={e => set("uei", e.target.value.toUpperCase())}
                 placeholder="e.g. ABC123DEF456" />
        </div>
        <div>
          <label style={label}>CAGE Code (5 alphanumeric)</label>
          <input data-testid="fp-cage-input" style={input} maxLength={5}
                 value={profile.cage_code || ""} onChange={e => set("cage_code", e.target.value.toUpperCase())}
                 placeholder="e.g. 1A2B3" />
        </div>
        <div>
          <label style={label}>SAM.gov Status</label>
          <select data-testid="fp-samstatus" style={select}
                  value={profile.sam_status || "not_registered"}
                  onChange={e => set("sam_status", e.target.value)}>
            {SAM_STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Primary NAICS */}
      <div style={{ marginBottom: 18 }}>
        <label style={label}>Primary NAICS</label>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10 }}>
          <input data-testid="fp-primary-naics-code" style={input} maxLength={6}
                 value={profile.primary_naics?.code || ""}
                 onChange={e => setPrimaryFromCode(e.target.value.replace(/\D/g, ""))}
                 placeholder="6-digit code" />
          <select data-testid="fp-primary-naics-pick" style={select}
                  value={profile.primary_naics?.code || ""}
                  onChange={e => setPrimaryFromCode(e.target.value)}>
            <option value="">— Pick from catalog —</option>
            {catalog.map(n => (
              <option key={n.code} value={n.code}>{n.code} · {n.title}</option>
            ))}
          </select>
        </div>
        {profile.primary_naics?.title && (
          <div style={{ fontSize: 11, color: CHROME, marginTop: 4 }}>
            {profile.primary_naics.title}{profile.primary_naics.note ? ` · ${profile.primary_naics.note}` : ""}
          </div>
        )}
      </div>

      {/* Secondary NAICS */}
      <div style={{ marginBottom: 18 }}>
        <label style={label}>Secondary NAICS ({(profile.secondary_naics || []).length})</label>
        <div style={{ marginBottom: 8 }} data-testid="fp-secondary-list">
          {(profile.secondary_naics || []).map(s => (
            <span key={s.code} style={chip} data-testid={`fp-secondary-chip-${s.code}`}>
              <strong>{s.code}</strong>
              {s.title && <span style={{ color: CHROME }}>· {s.title.slice(0, 32)}{s.title.length > 32 ? "…" : ""}</span>}
              <button style={chipRemove} onClick={() => removeSecondary(s.code)} title="Remove" data-testid={`fp-remove-${s.code}`}>×</button>
            </span>
          ))}
          {(profile.secondary_naics || []).length === 0 && (
            <span style={{ color: CHROME, fontSize: 12 }}>No secondary codes yet.</span>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr auto", gap: 8 }}>
          <input data-testid="fp-add-secondary-input" style={input} maxLength={6}
                 value={newSecondaryCode}
                 onChange={e => setNewSecondaryCode(e.target.value.replace(/\D/g, ""))}
                 placeholder="6-digit" />
          <select data-testid="fp-add-secondary-pick" style={select}
                  value=""
                  onChange={e => setNewSecondaryCode(e.target.value)}>
            <option value="">— Or pick from catalog —</option>
            {catalog.map(n => (
              <option key={n.code} value={n.code}>{n.code} · {n.title}</option>
            ))}
          </select>
          <button data-testid="fp-add-secondary-btn" style={btnGhost} onClick={addSecondary}>+ Add</button>
        </div>
      </div>

      {/* Certifications */}
      <div style={{ marginBottom: 18 }}>
        <label style={label}>Small-business & M/WBE Certifications</label>
        <div data-testid="fp-cert-toggle-row">
          {DEFAULT_CERTS.map(c => {
            const on = (profile.certifications || []).includes(c);
            return (
              <button key={c}
                      data-testid={`fp-cert-${c.replace(/\W+/g, "_")}`}
                      onClick={() => toggleCert(c)}
                      style={{
                        ...chip,
                        cursor: "pointer",
                        background: on ? "rgba(34,197,94,0.18)" : "rgba(0,0,0,0.25)",
                        borderColor: on ? "#22c55e" : `${CHROME}44`,
                        color: on ? "#D1FAE5" : CHROME,
                        fontWeight: on ? 700 : 500,
                      }}>
                {on ? "✓ " : ""}{c}
              </button>
            );
          })}
        </div>
      </div>

      {/* SBA Size Standard */}
      <div style={{ marginBottom: 18, maxWidth: 320 }}>
        <label style={label}>SBA Size Standard (annual USD)</label>
        <input data-testid="fp-sba-size-input" style={input} type="number"
               value={profile.sba_size_standard_usd ?? 0}
               onChange={e => set("sba_size_standard_usd", Number(e.target.value))} />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button data-testid="fp-save-btn" style={btn} onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Federal Profile"}
        </button>
        {savedAt && <span data-testid="fp-saved-at" style={{ fontSize: 11, color: "#22c55e" }}>✓ Saved at {savedAt}</span>}
        {error && <span data-testid="fp-err" style={{ fontSize: 11, color: "#FCA5A5" }}>⚠ {error}</span>}
        {profile.updated_at && (
          <span style={{ fontSize: 10, color: CHROME, marginLeft: "auto" }}>
            last edited {new Date(profile.updated_at).toLocaleString()}
            {profile.updated_by_email ? ` · by ${profile.updated_by_email}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
